import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';
import { useFileUpload } from '../../hooks/shared/useFileUpload';
import {
  dialogContentStyles,
  dialogActionsStyles,
  importButtonStyles,
  IMPORT_COLORS,
} from '../../constants/importDialog';

// Sub-components
import ImportDialogHeader from './importDialog/ImportDialogHeader';
import FileUploadZone from './importDialog/FileUploadZone';
import SelectedFileDisplay from './importDialog/SelectedFileDisplay';
import TemplateDownloadSection from './importDialog/TemplateDownloadSection';
import ImportResultsDisplay from './importDialog/ImportResultsDisplay';

const ImportDialog = ({
  open,
  onClose,
  onImport,
  submitting = false,
  importResults = null,
}) => {
  const {
    selectedFile,
    dragActive,
    fileInputRef,
    setSelectedFile,
    handleFileSelect,
    handleDrag,
    handleDrop,
    resetFile,
    handleDropZoneClick,
  } = useFileUpload({
    errorMessage: 'Invalid file type. Please select a CSV or Excel file.',
  });

  const handleClose = () => {
    if (submitting) return;
    resetFile();
    onClose?.();
  };

  const handleImport = () => {
    if (selectedFile) {
      onImport?.(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: borderRadius.modal,
          boxShadow: shadows.xl,
          backgroundColor: colors.backgroundWhite,
        },
      }}
    >
      <ImportDialogHeader onClose={handleClose} submitting={submitting} />

      <DialogContent sx={dialogContentStyles}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
            mt: spacing.sm,
          }}
        >
          {importResults ? (
            <ImportResultsDisplay importResults={importResults} />
          ) : (
            <>
              <FileUploadZone
                selectedFile={selectedFile}
                dragActive={dragActive}
                fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleDropZoneClick}
              />

              {selectedFile && <SelectedFileDisplay file={selectedFile} onRemove={handleRemoveFile} />}

              <TemplateDownloadSection />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={dialogActionsStyles}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            borderRadius: borderRadius.button,
            padding: `${spacing.xs} ${spacing.md}`,
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightMedium,
          }}
        >
          {importResults ? 'Close' : 'Cancel'}
        </Button>
        {!importResults && (
          <Button
            onClick={handleImport}
            disabled={submitting || !selectedFile}
            startIcon={
              submitting ? (
                <CircularProgress size={16} sx={{ color: IMPORT_COLORS.DARK_TEXT }} />
              ) : (
                <CloudUploadIcon />
              )
            }
            sx={importButtonStyles}
          >
            {submitting ? 'Importing...' : 'Import Users'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
